import ClearIcon from "@mui/icons-material/ClearOutlined";
import { Box, IconButton, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import TabPanel from "../../components/common/TabPanel";
import Title from "../../components/common/Title";
import { ValidationAlert } from "../../components/common/ValidationAlert";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import useAlert from "../../hooks/useAlert";
import usePersistedState from "../../hooks/usePersistedState";
import Auto4clientLogsTable from "./components/Auto4clientLogsTable";
import DisplayTabs from "./components/DisplayTabs";
import ErrorLogsTable from "./components/ErrorLogsTable";
import RefreshButton from "./components/RefreshButton";
import ScraperLogsTable from "./components/ScraperLogsTable";
import TaskLogsTable from "./components/TaskLogsTable";
import { DEFAULT_STATES, LOG_TABS } from "./constants/logConstants";
import { useLogs } from "./hooks/useLogs";

function Logs() {
  const {
    logs: { mongoTaskLogs, scraperLogStreams, auto4clientLogStreams, errorList },
    fetchAllLogs,
    fetchMongoTaskLogs,
    fetchScraperLogStreams,
    fetchAuto4clientLogStreams,
    fetchErrorList,
  } = useLogs();
  const [displayTab, setDisplayTab] = usePersistedState(
    "logs.displayTab",
    DEFAULT_STATES.DISPLAY_TAB,
    Object.values(LOG_TABS),
  );
  const [filterQuery, setFilterQuery] = useState(DEFAULT_STATES.FILTER);
  const { alertState, showAlert, closeAlert } = useAlert();

  const clearSearch = () => setFilterQuery("");
  const applyFilter = (e) => setFilterQuery(e.target.value);

  useEffect(() => {
    fetchAllLogs();
  }, [fetchAllLogs]);

  return (
    <Layout>
      <ValidationAlert
        inputOpen={alertState.alertOpen}
        setInputClosed={closeAlert}
        severity={alertState.alertType}
        alertMessage={alertState.alertMsg}
      />

      <TopBarGridContainer>
        {/* Display tabs */}
        <Grid size="grow">
          <Box display="flex" justifyContent="end">
            {/* Task logs / Log streams / Auto4client / Errors */}
            <DisplayTabs
              displayTab={displayTab}
              setDisplayTab={setDisplayTab}
            />

            {/* Refresh Button */}
            <RefreshButton
              fetchAuto4clientLogStreams={fetchAuto4clientLogStreams}
              fetchScraperLogStreams={fetchScraperLogStreams}
              fetchMongoTaskLogs={fetchMongoTaskLogs}
              fetchErrorList={fetchErrorList}
            />
          </Box>
        </Grid>
      </TopBarGridContainer>

      <Box sx={{ pb: 5, px: { xs: 2, md: 5 }, width: "100%" }}>
        <Grid container justifyContent={"space-between"} sx={{ my: 2 }}>
          <Grid>
            <Title text={"Log Streams"} />
          </Grid>

          {/* search filter */}
          <Grid size={{ xs: 12, sm: "auto" }}>
            <TextField
              id="outlined-basic"
              label="Log Filter"
              variant="outlined"
              size="small"
              sx={{ width: { xs: "100%", sm: "500px" } }}
              onChange={applyFilter}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={clearSearch} edge="end">
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* Using TabPanel for each log type */}
        <TabPanel value={displayTab} index={LOG_TABS.TASK_LOGS}>
          <TaskLogsTable logs={mongoTaskLogs} filterQuery={filterQuery} />
        </TabPanel>

        <TabPanel value={displayTab} index={LOG_TABS.ERROR_LOGS}>
          <ErrorLogsTable
            logs={errorList}
            filterQuery={filterQuery}
            fetchErrorList={fetchErrorList}
            showAlert={showAlert}
          />
        </TabPanel>

        <TabPanel value={displayTab} index={LOG_TABS.SCRAPER_LOGS}>
          <ScraperLogsTable logs={scraperLogStreams} filterQuery={filterQuery} />
        </TabPanel>

        <TabPanel value={displayTab} index={LOG_TABS.AUTO4CLIENT_LOGS}>
          <Auto4clientLogsTable logs={auto4clientLogStreams} filterQuery={filterQuery} />
        </TabPanel>
      </Box>
    </Layout>
  );
}

export default Logs;
